import React from "react";
import { Form, Input, Space, FormInstance } from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";

import FormDrawer from "../common/formdrawer";
import CancelButton from "../common/cancelbutton";
import FormButton from "../common/formbutton";

import { Currency } from "../../types/currencytypes";

interface CurrencyFormDrawerProps {
    open: boolean;
    editing: Currency | null;
    form: FormInstance;
    onClose: () => void;
    onSave: () => void;
}

const MAX_CURRENCY_NAME_LENGTH = 100;
const MAX_SYMBOL_LENGTH = 10;

const CurrencyDrawer: React.FC<CurrencyFormDrawerProps> = ({
    open,
    editing,
    form,
    onClose,
    onSave
}) => {
    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    const footer = (
        <div style={{ textAlign: "right" }}>
            <Space>
                <CancelButton onClick={handleClose} />
                <FormButton
                    title={editing ? "Save" : "Create"}
                    icon={editing ? <EditOutlined /> : <PlusOutlined />}
                    onClick={() => {
                        form
                            .validateFields()
                            .then(() => onSave())
                            .catch(() => { });
                    }}
                />
            </Space>
        </div>
    );

    return (
        <FormDrawer
            title={editing ? "Edit Currency" : "Add Currency"}
            open={open}
            onClose={handleClose}
            onSave={() => { }}
            form={form}
            footer={footer}
        >

            <Form.Item
                name="currencyName"
                label="Currency Name"
                rules={[
                    { required: true, message: "Currency name is required" },
                    {
                        pattern: /^[A-Za-z ]+$/,
                        message: "Only alphabets and spaces are allowed"
                    },
                    {
                        max: MAX_CURRENCY_NAME_LENGTH,
                        message: `Name cannot exceed ${MAX_CURRENCY_NAME_LENGTH} characters`
                    }
                ]}
            >
                <Input
                    maxLength={MAX_CURRENCY_NAME_LENGTH}
                    showCount
                    placeholder="e.g. Norwegian Krone"
                    onChange={(e) =>
                        form.setFieldValue(
                            "currencyName",
                            e.target.value.replace(/[^A-Za-z ]/g, "")
                        )
                    }
                />
            </Form.Item>

            <Form.Item
                name="currencyCode"
                label="Currency Code (ISO 4217)"
                rules={[
                    { required: true, message: "Currency code is required" },
                    {
                        pattern: /^[A-Z]{3}$/,
                        message: "Must be a 3-letter ISO code (e.g. USD, EUR)"
                    }
                ]}
            >
                <Input
                    maxLength={3}
                    placeholder="e.g. USD"

                    onChange={(e) =>
                        form.setFieldValue(
                            "currencyCode",
                            e.target.value
                                .replace(/[^a-zA-Z]/g, "")
                                .toUpperCase()
                        )}

                />
            </Form.Item>


            <Form.Item
                name="currencyNumber"
                label="Currency Number (ISO 4217)"
                rules={[
                    { required: true, message: "Currency number is required" },
                    {
                        pattern: /^[0-9]{3}$/,
                        message: "Currency number must be exactly 3 digits (e.g. 840)"
                    }
                ]}
            >
                <Input
                    inputMode="numeric"
                    maxLength={3}
                    placeholder="e.g. 840"
                />
            </Form.Item>

            <Form.Item
                name="currencySymbol"
                label="Currency Symbol"
                rules={[
                    { required: true, message: "Currency symbol is required" },
                    {
                        pattern: /^[^\d]+$/,
                        message: "Currency symbol must not contain numbers"
                    },
                    {
                        max: MAX_SYMBOL_LENGTH,
                        message: `Symbol cannot exceed ${MAX_SYMBOL_LENGTH} characters`
                    }
                ]}
            >
                <Input
                    maxLength={MAX_SYMBOL_LENGTH}
                    showCount
                    placeholder="e.g. $, €, kr"
                />
            </Form.Item>

        </FormDrawer>
    );
};

export default CurrencyDrawer;